<template>
  <div>
    <v-row class="mt-3">
      <v-col cols="3" sm="6" md="4">
        <analyze-search v-model="search" :hide-details="true" />
      </v-col>
      <v-col>
        <v-btn
          id="create-new-custom-metric-btn px-8"
          color="cyan-dark"
          class="ml-5 create-custom-metric-btn"
          variant="flat"
          :prepend-icon="CONSTANTS.ICONS_MDI.ICON_EDIT_OUTLINE"
          @click="createNewCustomMetric"
        >
          {{ labels.CUSTOM_METRIC.CREATE_NEW_CUSTOM_METRIC }}
        </v-btn>
      </v-col>
    </v-row>
    <div>
      <analyze-data-table
        generic="CustomMetric"
        table-id="custom-metrics-table-id"
        :headers="customMetricHeaders"
        :items="customMetricItems"
        :has-edit-action="true"
        :has-delete-action="true"
        :has-apply-action="hasApplyAction"
        :search="search"
        :total-items="customMetricItems.length"
        data-type="default"
        @edit="openCustomMetricsPageInEditMode"
        @delete="invokeDeletionDialog"
    /></div>
  </div>

  <ConfirmDeletionDialog
    v-if="selectedCustomMetricToDelete"
    :key="`dialog-delete-custom-metric${selectedCustomMetricToDelete?.id}`"
    :element-to-delete="selectedCustomMetricToDelete"
    :get-element-name="(p?: CustomMetric) => p?.name ?? 'My Custom Metric'"
    :element-type-name="labels.DELETE_ELEMENT.CUSTOM_METRIC"
    :is-deleting-element="isDeletingCustomMetric"
    :extra-confirmation-required="true"
    @confirm-deletion="deleteCustomMetric"
    @cancel-deletion="selectedCustomMetricToDelete = undefined"
  />
</template>

<script lang="ts" setup>
  import CONSTANTS from '@/constants/Config.constants'
  import labels from '@/translations/labels.json'
  import { computed, ref, watch } from 'vue'
  import ConfirmDeletionDialog from '@/components/ConfirmDeletionDialog/ConfirmDeletionDialog.vue'
  import { useProjectStore } from '@/stores/projectStore'
  import AnalyzeSearch from '@/components/Inputs/AnalyzeSearch.vue'
  import { parseIntUndefined } from '@/utils/helpers'
  import { storeToRefs } from 'pinia'
  import { useRoute } from 'vue-router'
  import AnalyzeDataTable from '@/components/AnalyzeDataTable/AnalyzeDataTable.vue'
  import { AnalyzeTableHeader, AnalyzeTableItem } from '@/models/table.model'
  import { useCustomMetricStore } from '@/stores/customMetricStore'
  import { CustomMetric } from '@/models/custom-metric.model'
  import { EventBus } from '@/event-bus'

  const customMetricStore = useCustomMetricStore()
  const { currentProject } = storeToRefs(useProjectStore())
  const emit = defineEmits<{
    edit: [metric: CustomMetric]
    apply: [metric: CustomMetric]
  }>()

  const route = useRoute()

  const search = ref<string>('')

  defineProps<{
    hasApplyAction?: boolean
  }>()

  const selectedCustomMetricToDelete = ref<CustomMetric | undefined>()
  const isDeletingCustomMetric = ref(false)

  const customMetricHeaders = ref<AnalyzeTableHeader[]>([
    { title: '', key: 'selected', width: '58', sortable: false },
    { title: 'Name', key: 'name' },
    { title: '', key: 'actions', align: 'end', sortable: false },
  ])

  const customMetricItems = computed((): AnalyzeTableItem<CustomMetric>[] =>
    customMetricStore.customMetrics.map((customMetric) => ({
      name: customMetric.name,
      value: customMetric,
      selected: customMetric.id === customMetricStore.selectedCustomMetricId,
    })),
  )

  watch(
    currentProject,
    (project) => {
      if (project) {
        customMetricStore.loadCustomMetricsFromApi(
          project.id,
          parseIntUndefined(route.query.customMetric as string),
        )
      }
    },
    { immediate: true },
  )

  function createNewCustomMetric(): void {
    EventBus.emit('createNewCustomMetric')
  }

  function openCustomMetricsPageInEditMode(metric: CustomMetric): void {
    if (metric) {
      emit('edit', metric)
    }
  }

  function invokeDeletionDialog(item: CustomMetric): void {
    if (item) {
      selectedCustomMetricToDelete.value = item
    }
  }

  async function deleteCustomMetric(): Promise<void> {
    if (!currentProject.value || !selectedCustomMetricToDelete.value) {
      return Promise.resolve()
    }

    isDeletingCustomMetric.value = true

    if (
      selectedCustomMetricToDelete.value &&
      selectedCustomMetricToDelete.value.id === customMetricStore.selectedCustomMetricId
    ) {
      customMetricStore.applyCustomMetric(undefined)
    }

    await customMetricStore.deleteCustomMetric(
      currentProject.value.id,
      selectedCustomMetricToDelete.value,
    )

    isDeletingCustomMetric.value = false
    selectedCustomMetricToDelete.value = undefined
  }
</script>

<style scoped>
  .create-metric-btn {
    text-transform: none;
    padding-left: 19px;
  }

  .metric-title {
    &:hover {
      cursor: pointer;
    }
  }

  .metric-icon {
    &:hover {
      cursor: pointer;
    }
  }

  .border-style {
    border-radius: 5px;
    padding-right: 10px;
    padding-top: 2px;
    padding-bottom: 2px;

    &:hover {
      background-color: rgb(var(--v-theme-grey-light));
    }
  }

  .custom-tooltip :deep(.v-overlay__content) {
    background: rgb(var(--v-theme-grey-light));
    color: rgb(var(--v-theme-font-color));
  }
</style>
